import { useState, useEffect } from 'react';
import { fetchPlans, updatePlan } from '../../../lib/prompt-admin-api';
import type { Plan } from '../../../lib/prompt-admin-api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { Loader2, Plus, Trash, Save } from 'lucide-react';

export function AdminPlans() {
    const [plans, setPlans] = useState<Plan[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState<string | null>(null);

    useEffect(() => {
        loadPlans();
    }, []);

    const loadPlans = async () => {
        try {
            const response = await fetchPlans();
            setPlans(response.plans);
        } catch (error) {
            console.error('Error fetching plans:', error);
            toast.error('Nie udało się pobrać planów');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePlan = async (plan: Plan) => {
        setSaving(plan.id);
        try {
            await updatePlan(plan.id, {
                name: plan.name,
                price: plan.price,
                credits: plan.credits,
                features: plan.features,
                checkout_url: plan.checkout_url,
                variant_id: plan.variant_id
            });
            toast.success(`Zaktualizowano plan ${plan.name}`);
        } catch (error) {
            console.error('Error updating plan:', error);
            toast.error('Błąd aktualizacji planu');
        } finally {
            setSaving(null);
        }
    };

    const handleFeatureChange = (planId: string, featureIndex: number, value: string) => {
        setPlans(plans.map(p => {
            if (p.id === planId) {
                const newFeatures = [...p.features];
                newFeatures[featureIndex] = value;
                return { ...p, features: newFeatures };
            }
            return p;
        }));
    };

    const handleAddFeature = (planId: string) => {
        setPlans(plans.map(p => {
            if (p.id === planId) {
                return { ...p, features: [...p.features, "Nowa funkcja"] };
            }
            return p;
        }));
    };

    const handleRemoveFeature = (planId: string, featureIndex: number) => {
        setPlans(plans.map(p => {
            if (p.id === planId) {
                const newFeatures = p.features.filter((_, i) => i !== featureIndex);
                return { ...p, features: newFeatures };
            }
            return p;
        }));
    };

    if (loading) {
        return <div className="flex justify-center p-8"><Loader2 className="animate-spin w-8 h-8" /></div>;
    }

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight">Plany Subskrypcji</h2>
                    <p className="text-muted-foreground">
                        Zarządzaj cenami, limitami i linkami do Lemon Squeezy.
                    </p>
                </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
                {plans.map(plan => (
                    <Card key={plan.id} className="flex flex-col">
                        <CardHeader>
                            <CardTitle>{plan.name} ({plan.id})</CardTitle>
                            <CardDescription>
                                Variant ID: {plan.variant_id}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4 flex-1">
                            <div className="space-y-2">
                                <Label>Nazwa wyświetlana</Label>
                                <Input
                                    value={plan.name}
                                    onChange={e => setPlans(plans.map(p => p.id === plan.id ? { ...p, name: e.target.value } : p))}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Cena (PLN)</Label>
                                    <Input
                                        type="number"
                                        value={plan.price}
                                        onChange={e => setPlans(plans.map(p => p.id === plan.id ? { ...p, price: parseInt(e.target.value) || 0 } : p))}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Kredyty</Label>
                                    <Input
                                        type="number"
                                        value={plan.credits}
                                        onChange={e => setPlans(plans.map(p => p.id === plan.id ? { ...p, credits: parseInt(e.target.value) || 0 } : p))}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Checkout URL (UUID Link)</Label>
                                <Input
                                    className="font-mono text-xs"
                                    value={plan.checkout_url}
                                    onChange={e => setPlans(plans.map(p => p.id === plan.id ? { ...p, checkout_url: e.target.value } : p))}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Cechy (Features)</Label>
                                <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
                                    {plan.features.map((feature, idx) => (
                                        <div key={idx} className="flex gap-2">
                                            <Input
                                                value={feature}
                                                onChange={e => handleFeatureChange(plan.id, idx, e.target.value)}
                                                className="h-8 text-sm"
                                            />
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                                                onClick={() => handleRemoveFeature(plan.id, idx)}
                                            >
                                                <Trash className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    ))}
                                    <Button variant="outline" size="sm" className="w-full h-8 dashed border-dashed" onClick={() => handleAddFeature(plan.id)}>
                                        <Plus className="w-3 h-3 mr-1" /> Dodaj cechę
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                        <CardFooter>
                            <Button
                                className="w-full"
                                onClick={() => handleUpdatePlan(plan)}
                                disabled={saving === plan.id}
                            >
                                {saving === plan.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                                Zapisz zmiany
                            </Button>
                        </CardFooter>
                    </Card>
                ))}
            </div>
        </div>
    );
}
